import type { Pub, PubDatabase } from './types.js';

/** Parse the authoritative visits.md document into map data. */
export function parseVisitsMarkdown(markdown: string): PubDatabase {
  const lines = markdown.split(/\r?\n/);
  const pubs: Pub[] = [];

  for (let index = 0; index < lines.length; index++) {
    if (lines[index].trim().startsWith('### ')) {
      pubs.push(parsePubSection(lines, index));
    }
  }

  return pubs;
}

function nextContentLines(lines: string[], startIndex: number, count: number): string[] {
  const content: string[] = [];

  for (let index = startIndex + 1; index < lines.length && content.length < count; index++) {
    const line = lines[index].trim();
    if (line.startsWith('### ')) {
      break;
    }
    if (line !== '') {
      content.push(line);
    }
  }

  return content;
}

/** Parse one pub section beginning at its `###` heading. */
export function parsePubSection(lines: string[], startIndex: number): Pub {
  const nameLine = lines[startIndex]?.trim() ?? '';
  if (!nameLine.startsWith('### ')) {
    throw new Error(`Invalid pub heading at line ${startIndex + 1}.`);
  }

  const content = nextContentLines(lines, startIndex, 3);
  if (content.length !== 3) {
    throw new Error(`Incomplete pub section at line ${startIndex + 1}: ${nameLine}`);
  }

  const { visited, date } = parseStatusLine(content[0], startIndex + 2);
  const { address, town, postcode } = parseAddressLine(content[1], startIndex + 3);
  const { lat, lng } = parseCoordinates(content[2], startIndex + 4);

  return {
    postcode,
    name: nameLine.slice(4).trim(),
    address,
    town,
    lat,
    lng,
    visited,
    firstVisit: date,
    lastVisit: date,
  };
}

/** Parse `✅ visited on: YYYY-MM-DD`, `✅ visited on: -`, or `❌ visited on: -`. */
export function parseStatusLine(
  line: string,
  lineNumber: number
): { visited: boolean; date: string | null } {
  const match = /^(✅|❌) visited on:\s*(\d{4}-\d{2}-\d{2}|-)$/.exec(line);
  if (!match) {
    throw new Error(`Invalid status line at line ${lineNumber}: ${line}`);
  }

  const visited = match[1] === '✅';
  const date = match[2] === '-' ? null : match[2];

  if (!visited && date !== null) {
    throw new Error(`Invalid status line at line ${lineNumber}: unvisited pub has a date`);
  }

  return { visited, date };
}

/** Parse an address line ending in a Markdown hard-break backslash. */
export function parseAddressLine(
  line: string,
  lineNumber: number
): { address: string; town: string; postcode: string } {
  if (!line.endsWith('\\')) {
    throw new Error(`Invalid address line at line ${lineNumber}: must end with backslash`);
  }

  const parts = line
    .slice(0, -1)
    .split(',')
    .map((part) => part.trim());
  if (parts.length < 3 || parts.some((part) => part === '')) {
    throw new Error(`Invalid address line at line ${lineNumber}: ${line}`);
  }

  return {
    address: parts.slice(0, -2).join(', '),
    town: parts.at(-2)!,
    postcode: parts.at(-1)!,
  };
}

/** Parse and range-check a `(latitude, longitude)` line. */
export function parseCoordinates(line: string, lineNumber: number): { lat: number; lng: number } {
  const match = /^\((-?(?:\d+(?:\.\d+)?|\.\d+)),\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\)$/.exec(
    line
  );
  if (!match) {
    throw new Error(`Invalid coordinates at line ${lineNumber}: ${line}`);
  }

  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (lat < -90 || lat > 90) {
    throw new Error(`Latitude out of range at line ${lineNumber}: ${lat}`);
  }
  if (lng < -180 || lng > 180) {
    throw new Error(`Longitude out of range at line ${lineNumber}: ${lng}`);
  }

  return { lat, lng };
}
